'use client';

import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { Icon } from '@/components/ui/icon/icon';
import type { IconName } from '@/components/ui/icon/icon-registry';
import { Label } from './label';
import styles from './form-field.module.css';

type FormFieldProps = {
  /** The ROOT div's own id — e.g. for a caller that needs to `scrollIntoView`
   * a specific field (a rejected save landing the reader on the first error).
   * Unrelated to `controlId` below, which is always generated/read separately
   * for the label/control association. */
  id?: string;
  label?: ReactNode;
  /** Forwarded to the rendered `Label`'s own `size` prop — see its doc
   * comment. Only meaningful when `label` is set. */
  labelSize?: 'sm' | 'lg';
  /**
   * Keeps `label` as the control's accessible name without drawing it —
   * for a control whose surrounding context already labels it visibly, so
   * the label text isn't duplicated on screen. See `.srOnly`'s doc comment
   * in `form-field.module.css`.
   */
  hideLabel?: boolean;
  top?: ReactNode;
  hint?: ReactNode;
  /**
   * Where `hint` is drawn. Defaults to `'label'` — directly under the label,
   * above the control, so the reader sees the guidance before they fill the
   * field and an error below the control no longer replaces it. Pass
   * `'control'` for text that reacts to the value (e.g. a "N characters
   * remaining" counter), which reads better next to what it counts.
   */
  hintPlacement?: 'label' | 'control';
  error?: ReactNode;
  children: ReactNode;
  iconStart?: IconName | '';
  iconEnd?: IconName | '';
  iconEndClickable?: boolean;
  onIconEndClick?: () => void;
  iconEndDisabled?: boolean;
  /**
   * Accessible name for the clickable end icon. Defaults to the old generic
   * "icon action", which says nothing about what the button does — pass a real
   * name (e.g. "Clear search") whenever `iconEndClickable` is set.
   */
  iconEndLabel?: string;
  hasAsterisk?: boolean;
  isRequired?: boolean;
  hasError?: boolean;
  className?: string;
};

export function FormField({
  id,
  label,
  labelSize,
  hideLabel = false,
  top,
  hint,
  hintPlacement = 'label',
  error,
  children,
  iconStart,
  iconEnd,
  iconEndClickable = false,
  onIconEndClick,
  iconEndDisabled = false,
  iconEndLabel = 'icon action',
  hasAsterisk,
  isRequired,
  hasError = false,
  className,
}: Readonly<FormFieldProps>) {
  const required = isRequired ?? hasAsterisk ?? false;
  const rootClassName = [
    styles.formField,
    iconStart ? styles.iconStart : '',
    hasError ? styles.hasError : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedIconEnd = iconEnd || '';

  // Associate the label with its control via id/htmlFor so getByLabelText
  // (and native "click label to focus" behavior) works. An id the caller
  // already set — on a plain DOM element or a Controller-wrapped custom one
  // (MultiSelectAutocomplete, SingleSelectDropdown, etc.) — is used as-is.
  // Only a bare DOM element with no id of its own gets one generated and
  // cloned on; a custom component's internals are otherwise left untouched.
  const generatedId = useId();
  const existingId = isValidElement(children) ? (children.props as { id?: string }).id : undefined;
  const shouldGenerateId =
    isValidElement(children) && typeof children.type === 'string' && !existingId;
  const controlId = existingId ?? (shouldGenerateId ? generatedId : undefined);
  const renderedChildren =
    shouldGenerateId && isValidElement(children)
      ? cloneElement(children as ReactElement<{ id?: string }>, { id: controlId })
      : children;

  return (
    <div
      id={id}
      className={rootClassName}
      data-testid="form-field"
      data-required={required ? 'true' : undefined}
      data-error={hasError ? 'true' : undefined}
    >
      <div className={styles.labelHeader}>
        {label ? (
          // `Label` owns the type, the colour and the asterisk. FormField keeps
          // only the wiring it is uniquely able to do — handing over the id it
          // generated for the control below.
          <Label
            htmlFor={controlId}
            required={required}
            size={labelSize}
            className={hideLabel ? styles.srOnly : undefined}
          >
            {label}
          </Label>
        ) : null}
        {top ? <small className={styles.topSlot}>{top}</small> : null}
        {hint && hintPlacement === 'label' ? (
          <small className={styles.topSlot} data-hint>
            {hint}
          </small>
        ) : null}
      </div>

      <div className={styles.control}>
        {iconStart ? (
          <span className={styles.iconStartSlot}>
            <Icon name={iconStart} label={iconStart} size="sm" />
          </span>
        ) : null}

        {renderedChildren}

        <span className={styles.iconEndSlot}>
          {resolvedIconEnd ? (
            iconEndClickable ? (
              <button
                type="button"
                className={styles.iconButton}
                onClick={onIconEndClick}
                disabled={iconEndDisabled}
                aria-label={iconEndLabel}
              >
                <Icon name={resolvedIconEnd} label={resolvedIconEnd} size="sm" />
              </button>
            ) : (
              <Icon name={resolvedIconEnd} label={resolvedIconEnd} size="sm" />
            )
          ) : null}
        </span>
      </div>

      {error ? (
        <small className={styles.bottomSlot} data-bottom>
          {error}
        </small>
      ) : hint && hintPlacement === 'control' ? (
        <small className={styles.bottomSlot} data-bottom data-hint>
          {hint}
        </small>
      ) : null}
    </div>
  );
}
